import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { JobTaskService, type JobTasksFilters } from '../api/job-task-service';
import type { PaginationDto } from '../types/common';
import type { JobTask, ListJobTasksResponse } from '../types/job-task';

export const jobTaskQueryKeys = {
  all: ['job-tasks'] as const,
  list: (filters: JobTasksFilters, pagination: PaginationDto = {}) =>
    ['job-tasks', 'list', filters, pagination] as const,
  byId: (id: string | null) => ['job-tasks', id] as const,
};

export function useJobTasks(
  filters: JobTasksFilters,
  pagination?: PaginationDto,
  refetchInterval?: number | false,
): UseQueryResult<ListJobTasksResponse> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: jobTaskQueryKeys.list(filters, pagination),
    queryFn: async () => {
      const result = await JobTaskService.getJobTasks(filters, pagination);
      for (const task of result.items) {
        queryClient.setQueryData(jobTaskQueryKeys.byId(task._id), task);
      }
      return result;
    },
    refetchInterval,
  });
}

export function useJobTask(id: string | null, accountId: string | null): UseQueryResult<JobTask> {
  return useQuery({
    queryKey: jobTaskQueryKeys.byId(id),
    queryFn: () => JobTaskService.getJobTask(id as string, accountId as string),
    enabled: Boolean(id) && Boolean(accountId),
  });
}
